/**
 * 工具能力（`#tools`）：plan 实际在用的工具清单（用户 10-20：「应答配置里面为啥没有工具？……就是之前的 skill 菜单」）。
 * 照 Loop 模式「SKILL」页（SkillRefTab：数据源表 ＋ 每个 skill 的输入 ／ 逻辑 ／ 样例入参与输出）拷一份到 plan，改成：
 * 一张表列全部工具（读写、角色、给 N2 的一句话、必填、要 N2 说出的选择、当前后端、挂的意图、接入检查），点行开抽屉看
 * 契约 ／ 召回画像 ／ 返回字段 ／ 接入检查 ／ 样例。样例只用演示数据试调用（后端强制不走 live，写工具只预演、不提交）。
 * 不拷 Loop 的后端切换、清空存档、店铺连接（plan 侧没有写接口，评测要求全演示数据）。只读，改工具走 PR。
 */
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { JsonBlock } from "@/components/ui/code-block";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import {
	type OnboardingRow,
	type PlanCapability,
	type PlanLayer,
	type PlanTool,
	planErrorMessage,
	usePlanToolSample,
	usePlanTools,
	usePlanToolsOnboarding,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import {
	LAYER_ALL,
	LAYER_FALLBACK,
	LayerBadge,
	LayerDetail,
	layerOptions,
	matchLayer,
} from "../kit/layer";
import { PageJump } from "../kit/page-jump";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";

const ALL = "__all__";
const STANDING = "__standing__";

const TYPE_LABEL: Record<string, string> = { READ: "读", WRITE: "写", GENERIC: "通用" };
/** 执行前置的依据（画像 pv14 起）：客观做不到 ／ 跟业务包默认可办窗口走（窗口放宽就不再生效） */
const BASIS_ZH: Record<"objective" | "window", string> = {
	objective: "客观做不到",
	window: "默认窗口，见业务参数 platform.write_windows",
};
const TYPE_TONE: Record<string, BadgeTone> = { READ: "info", WRITE: "warning", GENERIC: "muted" };
const ROLE_LABEL: Record<string, string> = {
	lookup: "查单",
	handoff: "转人工",
	calculator: "计算器",
	recommender: "推荐",
};
/** 环节短名（同字段契约页）；全称悬停看 */
const CONSUMER_SHORT: Record<string, string> = {
	facts: "事实表",
	n2: "N2",
	n3: "N3",
	signal: "信号",
	code: "代码",
};
const BACKEND_LABEL: Record<string, string> = {
	fixture: "演示数据",
	recorded: "录制回放",
	live: "真实店铺",
};

type Check = { failed: OnboardingRow[]; rows: OnboardingRow[] };

function CheckCell({ check, loading }: { check?: Check; loading: boolean }) {
	if (!check) return <span className="text-text-muted">{loading ? "…" : "—"}</span>;
	if (!check.failed.length) return <Badge tone="success">通过</Badge>;
	return (
		<span title={check.failed.map((r) => `${r.desc}${r.note ? `：${r.note}` : ""}`).join("\n")}>
			<Badge tone="danger">{check.failed.length} 项不过</Badge>
		</span>
	);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="whitespace-pre-wrap break-words text-sm text-text-primary">{children}</div>
		</div>
	);
}

function Tags({ items, mono = false }: { items: string[]; mono?: boolean }) {
	if (!items.length) return <span className="text-text-muted">无</span>;
	return (
		<span className="flex flex-wrap gap-1">
			{items.map((t) => (
				<Badge key={t} tone="muted" className={mono ? "font-mono" : undefined}>
					{t}
				</Badge>
			))}
		</span>
	);
}

/** 工具的层记在召回画像上（契约和说明分开时按画像记） */
const layerRow = (t: PlanTool) => ({
	layer: t.profile?.layer,
	layer_notes: t.profile?.layer_notes,
});

const CAP_KIND_ZH: Record<string, string> = { write: "写", read: "只读", handoff: "转人工" };
const OBJECT_ZH: Record<string, string> = { order: "订单", line_item: "行项" };

/** 能力清单（#678）：系统能做哪些动作——不手写，由工具注册表、工具说明、店铺知识约束生成；回复旁检（REP-10）与能力对齐闸读它 */
function CapabilityList({
	store,
	rows,
}: {
	store: string;
	rows: PlanCapability[];
}) {
	const cols: PlanColumn<PlanCapability>[] = [
		{
			key: "tool",
			title: "工具",
			className: "w-40",
			render: (r) => <span className="font-mono">{r.tool_id}</span>,
		},
		{
			key: "kind",
			title: "类别",
			className: "w-20",
			render: (r) => (
				<Badge tone={r.kind === "write" ? "warning" : "muted"}>
					{CAP_KIND_ZH[r.kind] ?? r.kind}
				</Badge>
			),
		},
		{ key: "action", title: "动作", className: "w-40", render: (r) => r.action || "—" },
		{
			key: "says",
			title: "客户常这么说",
			className: "max-w-[260px]",
			render: (r) => <CellTags items={r.says} max={2} />,
		},
		{
			key: "objects",
			title: "作用在",
			className: "w-24",
			render: (r) => (r.objects.length ? r.objects.map((o) => OBJECT_ZH[o] ?? o).join("、") : "—"),
		},
		{
			key: "available",
			title: `${store}能不能用`,
			className: "w-28",
			render: (r) =>
				r.available ? (
					<span className="text-text-secondary">能</span>
				) : (
					<Badge tone="warning">店铺不开放</Badge>
				),
		},
	];
	return (
		<div className="space-y-2 border-t border-surface-border pt-3" data-testid="plan-capabilities">
			<div className="flex flex-wrap items-baseline gap-2">
				<span className="text-sm font-semibold text-text-primary">能力清单</span>
				<span className="text-xs text-text-muted">
					系统能做哪些动作：由工具注册表、工具说明和店铺知识约束生成，不手写；店铺知识整条拦掉的写动作标「店铺不开放」。回复旁检「说了系统没有的动作」读它（规则{" "}
					<Link
						to="/plan-lab"
						search={{ rule: "REP-10" }}
						hash="guards"
						className="font-mono text-text-secondary underline-offset-2 hover:text-brand-accent hover:underline"
					>
						REP-10
					</Link>
					），能力对齐闸也按它核题。
				</span>
			</div>
			<PlanTable
				columns={cols}
				rows={rows}
				rowKey={(r) => r.tool_id}
				density="compact"
				rowTestId="plan-capability-row"
			/>
		</div>
	);
}

export function ToolsTab() {
	const q = usePlanTools();
	const onboard = usePlanToolsOnboarding();
	const [type, setType] = useState(ALL);
	const [intent, setIntent] = useState(ALL);
	const [backend, setBackend] = useState(ALL);
	const [layer, setLayer] = useState(LAYER_ALL);
	const [openName, setOpenName] = useState<string | null>(null);
	const data = q.data;
	const layers = data?.layers?.length ? data.layers : LAYER_FALLBACK;

	const checks = useMemo(() => {
		const out: Record<string, Check> = {};
		for (const r of onboard.data?.rows ?? []) {
			const c = out[r.tool] ?? { failed: [], rows: [] };
			out[r.tool] = c;
			c.rows.push(r);
			if (r.ok === false) c.failed.push(r);
		}
		return out;
	}, [onboard.data]);

	const intentName = (k: string) => data?.intents[k] ?? k;
	const usedIntents = useMemo(
		() => [...new Set((data?.tools ?? []).flatMap((t) => t.profile?.intents ?? []))],
		[data],
	);
	const rows = useMemo(
		() =>
			(data?.tools ?? []).filter(
				(t) =>
					(type === ALL || t.type === type) &&
					(backend === ALL || t.backend === backend) &&
					matchLayer(layer, layerRow(t), layers) &&
					(intent === ALL ||
						(intent === STANDING ? !!t.profile?.standing : !!t.profile?.intents.includes(intent))),
			),
		[data, type, backend, intent, layer, layers],
	);
	const open = data?.tools.find((t) => t.name === openName) ?? null;

	// 三态都打一级块标记 plan-block-tools（布局量测 C1～C3 靠它找块）
	if (q.isLoading)
		return (
			<Card data-testid="plan-block-tools">
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError || !data)
		return (
			<Card data-testid="plan-block-tools">
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-tools-fail"
					/>
				</CardContent>
			</Card>
		);

	const columns: PlanColumn<PlanTool>[] = [
		{
			key: "name",
			title: "工具",
			className: "w-40",
			render: (t) => <span className="font-mono">{t.name}</span>,
		},
		{
			key: "type",
			title: "读写",
			className: "w-16",
			render: (t) => (
				<Badge tone={TYPE_TONE[t.type] ?? "muted"}>{TYPE_LABEL[t.type] ?? t.type}</Badge>
			),
		},
		{
			key: "layer",
			title: "层",
			className: "w-24",
			render: (t) => <LayerBadge row={layerRow(t)} layers={layers} />,
		},
		{
			key: "role",
			title: "角色",
			className: "w-16",
			render: (t) =>
				t.role ? (ROLE_LABEL[t.role] ?? t.role) : <span className="text-text-muted">—</span>,
		},
		{
			key: "desc",
			title: "给 N2 的说明",
			className: "max-w-[220px]",
			render: (t) => (
				<span className="block truncate text-text-secondary" title={t.n2_desc}>
					{t.n2_desc}
				</span>
			),
		},
		{
			key: "required",
			title: "必填",
			className: "max-w-[180px]",
			render: (t) => <CellTags items={t.required} max={1} mono />,
		},
		{
			key: "select",
			title: "要说出的选择",
			className: "w-28",
			render: (t) => <CellTags items={t.select.map((s) => s.label)} />,
		},
		{
			key: "backend",
			title: "后端",
			className: "w-24",
			render: (t) => (
				<Badge tone={t.backend === "fixture" ? "muted" : "info"}>
					{BACKEND_LABEL[t.backend] ?? t.backend}
				</Badge>
			),
		},
		{
			key: "intents",
			title: data?.recall_mode === "relevance" ? "意图标签（只加分）" : "挂的意图",
			className: "max-w-[200px]",
			render: (t) =>
				t.profile ? (
					<CellTags
						items={[...(t.profile.standing ? ["常备"] : []), ...t.profile.intents.map(intentName)]}
					/>
				) : (
					<span className="text-text-muted">画像里没有</span>
				),
		},
		{
			key: "check",
			title: "接入检查",
			className: "w-24",
			render: (t) => <CheckCell check={checks[t.name]} loading={onboard.isLoading} />,
		},
	];
	const opts = (xs: string[], label: (x: string) => string = (x) => x) => [
		{ value: ALL, label: "全部" },
		...xs.map((x) => ({ value: x, label: label(x) })),
	];

	return (
		<Card data-testid="plan-block-tools">
			<CardContent className="space-y-3 py-4" data-testid="plan-tools">
				<div className="flex flex-wrap items-center gap-2">
					<LabeledSelect
						label="读写"
						value={type}
						onChange={setType}
						options={opts(["READ", "WRITE", "GENERIC"], (x) => TYPE_LABEL[x] ?? x)}
					/>
					<LabeledSelect
						label="意图"
						value={intent}
						onChange={setIntent}
						options={[
							...opts(usedIntents, intentName),
							{ value: STANDING, label: "常备（每件事都可用）" },
						]}
					/>
					<LabeledSelect
						label="后端"
						value={backend}
						onChange={setBackend}
						options={opts(data.backends, (x) => BACKEND_LABEL[x] ?? x)}
					/>
					<span data-testid="plan-tools-layer-filter">
						<LabeledSelect
							label="层"
							value={layer}
							onChange={setLayer}
							options={layerOptions(
								layers,
								data.tools.map((t) => layerRow(t)),
							)}
						/>
					</span>
					<span className="ml-auto text-xs text-text-muted">
						画像版本 <span className="font-mono">{data.profile_version}</span> · 共 {rows.length}{" "}
						个工具
					</span>
				</div>
				{rows.length ? (
					<PlanTable
						columns={columns}
						rows={rows}
						rowKey={(t) => t.name}
						onRowClick={(t) => setOpenName(t.name)}
						density="compact"
						rowTestId="plan-tool-row"
					/>
				) : (
					<EmptyState title="没有符合条件的工具" desc="换个读写类型、意图或后端看看。" />
				)}
				{data.capabilities ? (
					<CapabilityList store={data.capabilities.store_name} rows={data.capabilities.rows} />
				) : null}
			</CardContent>
			<Drawer
				open={!!open}
				onClose={() => setOpenName(null)}
				width="min(760px,95vw)"
				testId="plan-tool-drawer"
				title={<span className="font-mono">{open?.name}</span>}
				description={open?.n2_desc}
				badges={
					open ? (
						<>
							<Badge tone={TYPE_TONE[open.type] ?? "muted"}>
								{TYPE_LABEL[open.type] ?? open.type}
							</Badge>
							<Badge tone="muted">{BACKEND_LABEL[open.backend] ?? open.backend}</Badge>
						</>
					) : null
				}
			>
				{open ? (
					<ToolDetail
						tool={open}
						check={checks[open.name]}
						checkLoading={onboard.isLoading}
						consumers={data.consumers}
						intentName={intentName}
						profileVersion={data.profile_version}
						relevance={data.recall_mode === "relevance"}
						layers={layers}
					/>
				) : null}
			</Drawer>
		</Card>
	);
}

function ToolDetail({
	tool,
	check,
	checkLoading,
	consumers,
	intentName,
	profileVersion,
	relevance,
	layers,
}: {
	tool: PlanTool;
	check?: Check;
	checkLoading: boolean;
	consumers: Record<string, string>;
	intentName: (k: string) => string;
	profileVersion: string;
	relevance: boolean;
	layers: PlanLayer[];
}) {
	const p = tool.profile;
	const inputCols: PlanColumn<PlanTool["input"][number]>[] = [
		{
			key: "name",
			title: "字段",
			className: "w-44",
			render: (f) => <span className="font-mono">{f.name}</span>,
		},
		{
			key: "req",
			title: "必填",
			className: "w-16",
			render: (f) =>
				f.required ? (
					<Badge tone="warning">必填</Badge>
				) : (
					<span className="text-text-muted">可选</span>
				),
		},
		{
			key: "note",
			title: "说明",
			className: "max-w-[360px]",
			render: (f) => (
				<span className="block truncate text-text-secondary" title={f.note}>
					{f.note}
				</span>
			),
		},
	];
	const retCols: PlanColumn<PlanTool["returns"][number]>[] = [
		{
			key: "field",
			title: "字段",
			className: "w-40",
			render: (f) => <span className="font-mono">{f.field}</span>,
		},
		{
			key: "desc",
			title: "说明",
			className: "max-w-[240px]",
			render: (f) => {
				const text = f.object
					? `${f.object_desc ?? f.object}${f.list ? "列表" : ""}（按对象，${f.object_fields} 个字段）`
					: f.desc;
				return (
					<span
						className="block truncate text-text-secondary"
						title={f.note ? `${text}；${f.note}` : text}
					>
						{text}
						{f.sensitive ? "（敏感，不进模型）" : f.pii ? "（个人信息）" : ""}
					</span>
				);
			},
		},
		{
			key: "to",
			title: "给哪些环节",
			className: "max-w-[240px]",
			render: (f) =>
				f.object && !f.to.length ? (
					<span className="text-text-muted">按对象登记</span>
				) : (
					<span title={f.to.map((c) => consumers[c] ?? c).join("；")}>
						<CellTags items={f.to.map((c) => CONSUMER_SHORT[c] ?? c)} max={5} />
					</span>
				),
		},
	];

	return (
		<>
			<DrawerSection title="契约" testId="plan-tool-sec-contract">
				<Field label="给 N2 的一句话">{tool.n2_desc}</Field>
				{tool.n2_usage ? <Field label="使用协议（随候选给 N2）">{tool.n2_usage}</Field> : null}
				{tool.n2_params?.length ? (
					<Field label="N2 可直接带的入参（随候选给 N2）">
						<Tags items={tool.n2_params.map((x) => `${x.key} → ${x.path}：${x.note}`)} />
					</Field>
				) : null}
				<Field label="必填">
					{tool.required.length ? <Tags items={tool.required} mono /> : "无必填，可直接调"}
				</Field>
				{tool.input.length ? (
					<PlanTable
						columns={inputCols}
						rows={tool.input}
						rowKey={(f) => f.name}
						density="compact"
					/>
				) : (
					<Field label="输入字段">无入参</Field>
				)}
				<Field label="返回逻辑（数据条件 → 状态）">{tool.logic || "—"}</Field>
				<Field label="写操作确认门">
					{tool.confirm_gate
						? "走确认门：首次调用只出确认卡（预演），客户确认后才真提交"
						: "不走（不是写操作）"}
				</Field>
				<Field label="需要 N2 说出的选择">
					{tool.select.length ? (
						<span className="block space-y-1">
							{tool.select.map((s) => (
								<span key={s.kind} className="block">
									<span className="font-medium">{s.label}</span>
									<span className="font-mono text-text-muted">（{s.kind}）</span>：{s.from}
								</span>
							))}
						</span>
					) : (
						"无"
					)}
				</Field>
			</DrawerSection>

			<DrawerSection title="层" testId="plan-tool-sec-layer">
				<LayerDetail row={{ layer: p?.layer, layer_notes: p?.layer_notes }} layers={layers} />
			</DrawerSection>
			<DrawerSection title="召回画像" testId="plan-tool-sec-profile">
				{p ? (
					<>
						<div className="text-xs text-text-muted">
							生效版本 <span className="font-mono">{profileVersion}</span>
						</div>
						<Field label={relevance ? "意图标签（只加分，不作召回路）" : "挂的意图"}>
							{p.standing ? "常备工具（每件事项都可用）" : null}
							{p.intents.length ? (
								<Tags items={p.intents.map(intentName)} />
							) : p.standing ? null : (
								"无"
							)}
							<div className="mt-1 text-xs text-text-muted">
								意图的说明见 <PageJump to="tags" />
							</div>
						</Field>
						<Field label="能做什么">{p.what || "—"}</Field>
						<Field label="什么时候用">{p.when || "—"}</Field>
						{p.examples?.length ? (
							<Field label="客户常这么说（例句，写在「什么时候用」末尾）">
								<Tags items={p.examples} />
								<div className="mt-1 text-xs text-text-muted">
									召回按说明判相关度（含例句）；没召回到就改说明，不加对应关系
								</div>
							</Field>
						) : null}
						<Field label="什么时候不用">{p.when_not || "—"}</Field>
						{p.boundary ? <Field label="能力边界">{p.boundary}</Field> : null}
						<Field label="前置工具">
							<Tags items={p.prerequisites} mono />
						</Field>
						<Field label="前置状态（不满足就办不了）">
							{p.preconditions.length
								? p.preconditions
										.map(
											(c) => `${c.state}：${c.reason}${c.basis ? `（${BASIS_ZH[c.basis]}）` : ""}`,
										)
										.join("\n")
								: "无"}
							<div className="mt-1 text-xs text-text-muted">
								按窗口写的前置跟业务参数里的可办窗口走，值见 <PageJump to="business-params" />
							</div>
						</Field>
						<Field label="易混工具">
							<Tags items={p.confusable} mono />
						</Field>
						<Field label="用法（usage）">{p.usage || "—"}</Field>
					</>
				) : (
					<div className="text-sm text-text-muted">生效画像里没有这个工具，召回不到。</div>
				)}
			</DrawerSection>

			<DrawerSection
				title={
					<span className="flex items-center justify-between">
						<span>返回字段</span>
						<Link
							to="/plan-lab"
							hash="field-contract"
							className="text-xs font-normal text-text-secondary underline-offset-2 hover:text-brand-accent hover:underline"
							data-testid="plan-tool-to-field-contract"
						>
							去字段契约看全部
						</Link>
					</span>
				}
				testId="plan-tool-sec-returns"
			>
				{tool.returns.length ? (
					<PlanTable
						columns={retCols}
						rows={tool.returns}
						rowKey={(f) => f.field}
						density="compact"
					/>
				) : (
					<div className="text-sm text-text-muted">
						字段契约里没有登记这个工具自己的字段（只用通用字段）。
					</div>
				)}
			</DrawerSection>

			<DrawerSection title="接入检查" testId="plan-tool-sec-check">
				{check ? (
					<ul className="space-y-1.5 text-sm">
						{check.rows.map((r) => (
							<li
								key={r.item}
								className="flex items-start gap-2"
								data-testid="plan-tool-check-item"
							>
								<span className="w-14 shrink-0">
									{r.ok === null ? (
										<Badge tone="muted">人工核</Badge>
									) : r.ok ? (
										<Badge tone="success">通过</Badge>
									) : (
										<Badge tone="danger">不过</Badge>
									)}
								</span>
								<span className="text-text-primary">
									{r.desc}
									{r.note && r.ok !== null ? (
										<span className="text-text-muted">（{r.note}）</span>
									) : null}
								</span>
							</li>
						))}
					</ul>
				) : (
					<div className="text-sm text-text-muted">
						{checkLoading ? "检查中…" : "接入检查没加载出来"}
					</div>
				)}
			</DrawerSection>

			<SampleSection key={tool.name} tool={tool} />
		</>
	);
}

/** 样例（演示数据）：样例入参 ＋「用演示数据试调用」；后端强制走演示数据，写工具只预演不提交 */
function SampleSection({ tool }: { tool: PlanTool }) {
	const run = usePlanToolSample();
	return (
		<DrawerSection title="样例（演示数据）" testId="plan-tool-sec-sample">
			<div className="flex flex-wrap items-center gap-3">
				<Button
					type="button"
					variant="outline"
					size="sm"
					disabled={run.isPending}
					onClick={() => run.mutate(tool.name)}
					data-testid="plan-tool-try"
				>
					{run.isPending ? "调用中…" : "用演示数据试调用"}
				</Button>
				<span className="text-xs text-text-muted">
					{tool.confirm_gate
						? "写工具只做预演：不带确认，返回确认卡，不会提交"
						: "只读演示数据，不连真实店铺"}
				</span>
			</div>
			<Field label="样例入参">
				<JsonBlock value={tool.sample_input} maxHeight={200} />
			</Field>
			{run.isError ? (
				<div className="text-sm text-brand-danger" data-testid="plan-tool-try-fail">
					{planErrorMessage(run.error)}
				</div>
			) : null}
			{run.data ? (
				<div data-testid="plan-tool-try-result">
					<Field label="返回（演示数据）">
						<JsonBlock value={run.data.result} maxHeight={320} />
					</Field>
				</div>
			) : null}
		</DrawerSection>
	);
}
